import BigNumber from 'bignumber.js';
import moment from 'moment';
import { NumberRoundingDefinition, NumberRoundingMethod } from 'src/app/services/api.services';

export const MONTH_NAMES = [
  'January', 
  'February', 
  'March', 
  'April', 
  'May', 
  'June', 
  'July', 
  'August', 
  'September', 
  'October', 
  'November', 
  'December'
];

export const QUARTER_NAMES = [
  'Q1', 'Q2', 'Q3', 'Q4'
];

export const WEEKDAY_NAMES = [
  'Monday', 
  'Tuesday', 
  'Wednesday', 
  'Thursday', 
  'Friday', 
  'Saturday',
  'Sunday',
];

export const UTC_TIMEZONE = 'UTC';

const DAYS_PER_MONTH = 31;
const DAYS_PER_YEAR = 12 * DAYS_PER_MONTH;

const ONE_BASED_TWODIGIT_NAMES: string[] = [];
for (let i = 1; i <= 60; ++i) ONE_BASED_TWODIGIT_NAMES.push(('' + i).padStart(2, '0'));

const ZERO_BASED_TWODIGIT_NAMES: string[] = [];
for (let i = 0; i < 60; ++i) ZERO_BASED_TWODIGIT_NAMES.push(('' + i).padStart(2, '0'));

function makeValues(count: number, start = 0) {
  let array = [];

  for (let i = 0; i < count; ++i)
    array.push(start + i);

  return array;
}

export abstract class DateOrTimeValueBase<T extends DateOrTimeValueBase<T>> {
  constructor() { }

  public abstract get value(): number;

  public isSame(other: T) { return this.value === other.value; }
  public isNotSame(other: T) { return this.value !== other.value; }
  public isBefore(other: T) { return this.value < other.value; }
  public isSameOrBefore(other: T) { return this.value <= other.value; }
  public isAfter(other: T) { return this.value > other.value; }
  public isSameOrAfter(other: T) { return this.value >= other.value; }
}

export class YearValue extends DateOrTimeValueBase<YearValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return '' + (this.index + 1); }

  public toString() {
    return this.name;
  }
}

export class QuarterValue extends DateOrTimeValueBase<QuarterValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return QUARTER_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly q1 = new QuarterValue(0);
  public static readonly q2 = new QuarterValue(1);
  public static readonly q3 = new QuarterValue(2);
  public static readonly q4 = new QuarterValue(3);

  public static readonly values = [
    QuarterValue.q1,
    QuarterValue.q2,
    QuarterValue.q3,
    QuarterValue.q4
  ];
}

export class MonthValue extends DateOrTimeValueBase<MonthValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return MONTH_NAMES[this.index]; }
  public get periodName(): string { return ONE_BASED_TWODIGIT_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly january = new MonthValue(0);
  public static readonly february = new MonthValue(1);
  public static readonly march = new MonthValue(2);
  public static readonly april = new MonthValue(3);
  public static readonly may = new MonthValue(4);
  public static readonly june = new MonthValue(5);
  public static readonly july = new MonthValue(6);
  public static readonly august = new MonthValue(7);
  public static readonly september = new MonthValue(8);
  public static readonly october = new MonthValue(9);
  public static readonly november = new MonthValue(10);
  public static readonly december = new MonthValue(11);

  public static readonly values = [
    MonthValue.january,
    MonthValue.february,
    MonthValue.march,
    MonthValue.april,
    MonthValue.may,
    MonthValue.june,
    MonthValue.july,
    MonthValue.august,
    MonthValue.september,
    MonthValue.october,
    MonthValue.november,
    MonthValue.december,
  ];
}

export class MonthDayValue extends DateOrTimeValueBase<MonthDayValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return ONE_BASED_TWODIGIT_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly values = makeValues(31).map(i => new MonthDayValue(i));
}

export class WeekValue extends DateOrTimeValueBase<WeekValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return ONE_BASED_TWODIGIT_NAMES[this.index]; }

  public toString() {
    return '' + this.index;
  }

  public static readonly values = makeValues(24).map(i => new WeekValue(i));
}

export class WeekdayValue extends DateOrTimeValueBase<WeekValue>{
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return WEEKDAY_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly monday = new WeekdayValue(0);
  public static readonly tuesday = new WeekdayValue(1);
  public static readonly wednesday = new WeekdayValue(2);
  public static readonly thursday = new WeekdayValue(3);
  public static readonly friday = new WeekdayValue(4);
  public static readonly saturday = new WeekdayValue(5);
  public static readonly sunday = new WeekdayValue(6);
  
  public static readonly values = [
    WeekdayValue.monday,
    WeekdayValue.tuesday,
    WeekdayValue.wednesday,
    WeekdayValue.thursday,
    WeekdayValue.friday,
    WeekdayValue.saturday,
    WeekdayValue.sunday
  ];
}

export class HourValue extends DateOrTimeValueBase<HourValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return 1 + this.index; }
  public get name(): string { return ZERO_BASED_TWODIGIT_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly values = makeValues(24).map(i => new HourValue(i));
}

export class MinuteValue extends DateOrTimeValueBase<MinuteValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return this.index; }
  public get name(): string { return ZERO_BASED_TWODIGIT_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly values = makeValues(60).map(i => new MinuteValue(i));
}

export class SecondValue extends DateOrTimeValueBase<SecondValue> {
  constructor(
    public readonly index: number
  ) { super(); }

  public get value(): number { return this.index; }
  public get name(): string { return ZERO_BASED_TWODIGIT_NAMES[this.index]; }

  public toString() {
    return this.name;
  }

  public static readonly values = makeValues(60).map(i => new SecondValue(i));
}

export class TimeFractionValue extends DateOrTimeValueBase<TimeFractionValue> {
  constructor(
    public readonly value: number
  ) { super(); }

  private _name: string;
  public get name(): string { return this._name ??= this.value.toFixed(this.value > 0.999 ? 7 : 3); }

  public toString() {
    return this.name;
  }

  public static readonly zero = new TimeFractionValue(0);
}

export interface IDateOrTimeValue {
  toMoment(): moment.Moment;
}

export class DateValue extends DateOrTimeValueBase<DateValue> implements IDateOrTimeValue {
  constructor(
    public readonly year: YearValue,
    public readonly quarter: QuarterValue,
    public readonly month: MonthValue,
    public readonly day: MonthDayValue,
    public readonly week: WeekValue,
    public readonly weekday: WeekdayValue
  ) { super(); }

  public readonly value: number =
    this.year.index * DAYS_PER_YEAR +
    this.month.index * DAYS_PER_MONTH +
    this.day.index;
    
  public toSyntaxString() {
    return `date\'${this.toString()}\'`;
  }

  public toString() {
    return this.toMoment().toISOString(false);
  }

  public toMoment(): moment.Moment {
    let date = new Date(this.year.index, this.month.index, this.day.value);
    return moment.utc(date);
  }
}

export class TimeValue extends DateOrTimeValueBase<TimeValue> implements IDateOrTimeValue {
  constructor(
    public readonly hour: HourValue,
    public readonly minute: MinuteValue,
    public readonly second: SecondValue,
    public readonly fraction: TimeFractionValue
  ) { super(); }

  public readonly value: number =
    this.hour.index * 24 +
    this.minute.index * 60 +
    this.second.index * 60 +
    this.fraction.value;

  private _name: string;
  public get name(): string { return this._name ??= `${this.hour.name}:${this.hour.name}:${this.hour.name}.${this.fraction.name}`; }

  public get isAM(): boolean { return this.hour.index < 12; }
  public get isPM(): boolean { return !this.isAM; }
  
  public toSyntaxString() {
    return `time\'${this.toString()}\'`;
  }

  public toString() {
    return this.name;
  }

  public toMoment(): moment.Moment {
    let m = moment.utc();
    m.hour(this.hour.index);
    m.minute(this.minute.index);
    m.second(this.second.index);
    m.millisecond(this.fraction.value);
    return m;
  }
  
  public static readonly midnight = new TimeValue(
    HourValue.values[0],
    MinuteValue.values[0],
    SecondValue.values[0],
    TimeFractionValue.zero);
}

export class DateTimeValue extends DateOrTimeValueBase<DateTimeValue> implements IDateOrTimeValue {
  constructor(
    public readonly date: DateValue,
    public readonly time: TimeValue,
    public readonly timezone: string
  ) { super(); }

  public get value() { return this.toDate().getTime(); }

  private _isUtc: boolean;
  public isUtc(): boolean {
    return this._isUtc ??= !!(this.timezone === UTC_TIMEZONE || this.timezone.toUpperCase() == UTC_TIMEZONE);
  }

  public toDate(): Date {
    return this.toMoment().toDate();
  }

  public toMoment(): moment.Moment {
    let date = new Date(
      this.date.year.index, this.date.month.index, this.date.day.value,
      this.time.hour.index, this.time.minute.index, this.time.second.index, this.time.fraction.value);

    if (this.isUtc()) {
      return moment.utc(date);
    } else {
      return moment.tz(date, this.timezone);
    }
  }

  public toSyntaxString() {
    return `datetime\'${this.toString()}\'`;
  }

  public toString() {
    return this.toDate().toISOString();
  }
}

export type TimestampValue = DateValue | TimeValue | DateTimeValue;

export function createDateValue(from: any): DateValue | null;
export function createDateValue(year: number, month: number, day: number): DateValue | null;
export function createDateValue(from: any, month?: number, day?: number): DateValue | null {
  if (from instanceof DateValue)
    return from;
    
  if (typeof from === 'number') {
    from = new Date(from, month, day - 1);
  }

  let m = moment.isMoment(from) ? from.utc(true) : moment.utc(from);

  return new DateValue(
    new YearValue(m.year()),
    QuarterValue.values[m.quarter() - 1],
    MonthValue.values[m.month()],
    MonthDayValue.values[m.date() - 1],
    WeekValue.values[m.week() - 1],
    WeekdayValue.values[m.day() > 0 ? (m.day() - 1) : 6]);
}
  
export function createTimeValue(from: any): TimeValue | null;
export function createTimeValue(hour: number, min: number, sec: number, fract?: number): TimeValue | null;
export function createTimeValue(from: any, min?: number, sec?: number, fract?: number): TimeValue | null {
  if (from instanceof TimeValue)
    return from;

  if (typeof from === 'number') {
      return new TimeValue(
        HourValue.values[from],
        MinuteValue.values[min],
        SecondValue.values[sec],
        fract ? new TimeFractionValue(fract) : TimeFractionValue.zero);
  }

  if (from instanceof DateValue)
    return TimeValue.midnight;

  if (from instanceof DateTimeValue)
    return from.time;

  if (typeof from === 'string') {
    let split = from.split('.', 2);
    if (split.length > 0) {
      let split2 = split[0]?.split(':', 3);

      let hour  = parseInt(split2[0]);
      let min   = parseInt(split2[1]);
      let sec   = split2.length > 2 ? parseInt(split2[2]) : 0;
      let fract = split .length > 1 ? parseInt(split[1])  : 0;

      return new TimeValue(
        HourValue.values[hour],
        MinuteValue.values[min],
        SecondValue.values[sec],
        new TimeFractionValue(fract));
    }
  }
  
  if (from instanceof Date) 
    return new TimeValue(
      HourValue.values[from.getHours()],
      MinuteValue.values[from.getMinutes()],
      SecondValue.values[from.getSeconds()],
      new TimeFractionValue(from.getMilliseconds() / 1000.0));
  

  let m = moment.isMoment(from) ? from.utc(true) : moment.utc(from);

  return new TimeValue(
    HourValue.values[m.hour()],
    MinuteValue.values[m.minute()],
    SecondValue.values[m.second()],
    new TimeFractionValue(m.millisecond() / 1000.0));
}
  
export function createDateTimeValue(from: any, timezone: string): DateTimeValue | null;
export function createDateTimeValue(date: DateValue, time: TimeValue, timezone?: string): DateTimeValue | null;
export function createDateTimeValue(from: any, timeOrTimezone: string | TimeValue, timezone?: string): DateTimeValue | null {
  if (from instanceof DateTimeValue) {
    let tz = <string>timeOrTimezone;
    if (from.timezone.toLowerCase() !== tz.toLowerCase()) {
      let m = from.toMoment().tz(tz);

      return new DateTimeValue(
        createDateValue(m),
        createTimeValue(m),
        tz);
    }

    return from;
  }
    
  if (from instanceof DateValue) {
    if (timeOrTimezone instanceof TimeValue) {
      return new DateTimeValue(from, timeOrTimezone, timezone);
    }
    else {
      return new DateTimeValue(from, TimeValue.midnight, timeOrTimezone);
    }
  }

  let m = moment.isMoment(from) ? from : moment(from);

  return new DateTimeValue(
    createDateValue(m),
    createTimeValue(m),
    timeOrTimezone as string);
}
  
export function roundNumericValue(value: number, rounding: NumberRoundingDefinition): number {
  let { method, precision } = rounding;

  let result = value;

  let scale = (precision && precision != 1) ? Math.pow(10, precision) : null;
  if (scale !== null)
    result *= scale;

  switch (method) {
    case NumberRoundingMethod.Ceil:
      result = Math.ceil(result);
      break;

    case NumberRoundingMethod.Floor:
      result = Math.floor(result);
      break;
      
    case NumberRoundingMethod.AwayFromZero:
      result = value < 0 ? Math.round(result - 0.5) : Math.round(result + 0.5);
      break;

    case NumberRoundingMethod.Nearest:
      result = Math.round(result);
      break;

    case NumberRoundingMethod.Truncate:
      result = Math.trunc(result);
      break;
  }

  if (scale !== null)
    result /= scale;

  return result;
}

export function roundBigNumberValue(value: BigNumber, rounding: NumberRoundingDefinition): BigNumber {
  let { method, precision } = rounding;

  let result = value;

  let scale = (precision != 0 && precision != 1) ? Math.pow(10, precision) : null;
  if (scale !== null)
    result = result.multipliedBy(scale);

  result = result.integerValue(getBigNumberRounding(method));

  if (scale !== null)
    result = result.dividedBy(scale);

  return result;
}

export function getBigNumberRounding(method: NumberRoundingMethod) {
  return method == NumberRoundingMethod.AwayFromZero ? BigNumber.ROUND_UP :
         method == NumberRoundingMethod.Ceil         ? BigNumber.ROUND_CEIL :
         method == NumberRoundingMethod.Floor        ? BigNumber.ROUND_FLOOR :
         method == NumberRoundingMethod.Nearest      ? BigNumber.ROUND_HALF_DOWN :
         // Truncate
         BigNumber.ROUND_DOWN;
}
